/**
 * Content model for customer-specific paid-ad landing pages.
 *
 * These pages continue the story from a customer-testimonial video ad:
 * visitor watches a 15s/30s clip of a founder, clicks through, and lands
 * here to see the fuller story before applying for funding.
 *
 * Each brand gets one entry in CASE_STUDY_LPL_CONTENT keyed by slug.
 * Shared sections (testimonial grid, comparison, eligibility, logos, FAQ)
 * are reused as-is from components/profitmill-lpl.
 */

import { isLocaleAllowedForEnOnly } from "@/lib/locale-prefix";

/**
 * Darkens each background photo toward black at its top/bottom edges
 * (rather than a flat tint), so consecutive full-bleed images meet at a
 * shared dark band instead of a hard cut, reading as a soft merge
 * between them.
 */
export const CASE_STUDY_IMAGE_EDGE_FADE =
  "linear-gradient(to bottom, black 0%, rgba(0,0,0,0.55) 18%, rgba(0,0,0,0.55) 82%, black 100%)";

export interface CaseStudyStat {
  value: string;
  label: string;
}

export interface CaseStudyStorySection {
  kicker: string;
  title: string;
  blurb: string;
  /** Optional full-width background image behind this timeline stage. */
  backgroundSrc?: string;
}

export interface CaseStudySpokesperson {
  name: string;
  title: string;
  avatarSrc: string;
}

export interface CaseStudyLplContent {
  /** URL slug: page lives at /grow-with-wayflyer/{slug}. Must match its key in CASE_STUDY_LPL_CONTENT. */
  slug: string;
  metaTitle: string;
  metaDescription: string;
  brand: string;
  industry: string;
  location: string;
  heroTitle: string;
  heroHighlight: string;
  heroTitleSuffix: string;
  heroYouLine: string;
  heroSubhead: string;
  playbackId: string;
  posterWidth: number;
  posterHeight: number;
  /** Mobile (9:16 portrait) hero video for the scroll-expand hero — see
   * MOBILE_SCROLL_VIDEO_PLAYBACK_ID in hero.tsx. Falls back to that
   * shared default clip when unset, so most case studies don't need to
   * set this. */
  mobilePlaybackId?: string;
  spokesperson: CaseStudySpokesperson;
  challenge: CaseStudyStorySection;
  solution: CaseStudyStorySection;
  results: CaseStudyStorySection;
  resultsEyebrow: string;
  resultsQuote: string;
  resultsStats: CaseStudyStat[];
  /** Optional full-width background image behind the quote/stats callout. */
  resultsBackgroundSrc?: string;
  closingTitle: string;
  caseStudyHref: string;
}

export const SAND_CLOUD: CaseStudyLplContent = {
  slug: "sand-cloud",
  metaTitle: "How Sand Cloud Grew to $16.1M With Wayflyer | Wayflyer",
  metaDescription:
    "See how Sand Cloud used Wayflyer's flexible funding to grow beyond beach towels to $16.1M in revenue, without giving up equity.",
  brand: "Sand Cloud",
  industry: "Beach Lifestyle",
  location: "San Diego, CA",
  heroTitle: "Sand Cloud grew to",
  heroHighlight: "$16.1M",
  heroTitleSuffix: "without giving up equity.",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "Banks couldn't give Sand Cloud what it needed to grow. Wayflyer could. See how flexible funding took them from a single hero product to a full beach lifestyle brand, and what it could do for yours.",
  playbackId: "YeqAo02RI2VYmHSOHkdyeRC4trbhienre00nCasGvLPkA",
  posterWidth: 1280,
  posterHeight: 720,
  mobilePlaybackId: "IgEsdvBtX5kE501F005SxaqgsYGBVU5UEnsIB7ZIKra02w",
  spokesperson: {
    name: "Steven Ford",
    title: "Co-founder",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/0df6c3dcd68dd76e3af4b3dbf8fbaa78f272cac8-200x200.jpg",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "Big ideas but no cash to execute",
    blurb:
      "Every inventory order was bigger than the last, and the cash it swallowed kept growing with it. New product launches and retail expansion stayed on the shelf. Banks couldn't offer the amounts or terms to change that.",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender who finally got it",
    blurb:
      "Wayflyer moved at the speed the business needed, and understood its trajectory and seasonality, not just its paperwork. Capital that matched where Sand Cloud was headed, not where it had been.",
  },
  results: {
    kicker: "What's Possible For You",
    title: "More categories. More retail presence. Ideas turned into reality.",
    blurb:
      "With the capital freed up, Sand Cloud expanded into new categories, from bath towels and bags to a mineral sunscreen range, an expensive, unfamiliar bet that Wayflyer's funding made possible.",
  },
  resultsEyebrow: "Still growing together",
  resultsQuote:
    "We have all these great ideas and projects we want to keep building into. To have a partner like Wayflyer that can scale and grow with us as we do this is huge.",
  resultsStats: [
    { value: "$16.1M", label: "Annual revenue" },
    { value: "0%", label: "Equity given up" },
    { value: "3+", label: "New product lines funded" },
  ],
  closingTitle: "Ready to grow like Sand Cloud?",
  caseStudyHref: "/our-customers/sand-cloud",
};

export const CORREXIKO: CaseStudyLplContent = {
  slug: "correxiko",
  metaTitle:
    "How Correxiko Accessed 3-4x More Capital With Wayflyer | Wayflyer",
  metaDescription:
    "See how Correxiko funded rapid growth with three to four times more capital than any other lender could offer, fast, and without giving up equity.",
  brand: "Correxiko",
  industry: "Supplements",
  location: "UK",
  heroTitle: "Correxiko accessed",
  heroHighlight: "",
  heroTitleSuffix: "3-4x more capital",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "Correxiko had to stop marketing and turn away customers just to keep up. Other lenders called that too risky. Wayflyer looked at the trajectory and funded three to four times more, fast.",
  playbackId: "gzJo402jlVrKPax77J2aSnV5hDdeo92ttdyfchJ01dZ7I",
  posterWidth: 1280,
  posterHeight: 720,
  spokesperson: {
    name: "Dr. Dinuk Dissanayake",
    title: "Co-founder",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/1674ac3ebb32783cc3f3554c17b3b9950f46d80c-377x377.png",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "Growing so fast, they had to stop growing",
    blurb:
      "Sales took off almost overnight, but every product is made in-house, from ingredients like collagen imported from Canada, with lead times measured in months, not days. To keep up with existing customers, Correxiko had to stop marketing and turn away new ones entirely.",
    backgroundSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/3ece4771b707e26c65a04cdd12e5fbd0e1f6e850-1533x1076.jpg",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender who finally asked why",
    blurb:
      "Other lenders saw new customer acquisition as a risk they wouldn't fund. Wayflyer looked at Correxiko's trajectory and past performance, understood the difference between funding existing customers and funding new growth, and could see the numbers held up. They funded three to four times more than anyone else, fast.",
    backgroundSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/d91abc19c180f55b6325df92e36319cf807e42b7-1562x1071.jpg",
  },
  results: {
    kicker: "What's Possible For You",
    title: "Capital up front. Growth resumed.",
    blurb:
      "With the lump sum in hand, Correxiko could start marketing and acquiring new customers again, without pulling stock away from the customers they already had, and without giving up equity.",
    backgroundSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/e20a3afa85a896abe17450d4cde8664c5e90ebd1-1548x1074.jpg",
  },
  resultsEyebrow: "Back to full speed",
  resultsQuote:
    "We got the lump sum from Wayflyer in advance, with enough time to manufacture and bring new products to market. Right thing, right time.",
  resultsStats: [
    { value: "3-4x", label: "More capital than other lenders" },
    { value: "0%", label: "Equity given up" },
    { value: "24 hrs", label: "From offer to funds landing" },
  ],
  resultsBackgroundSrc:
    "https://cdn.sanity.io/images/1aendizt/redesign/30f5ce6ce31e34f4d34ce4556cdabd9b35e9fd7f-1984x1072.jpg",
  closingTitle: "Ready to grow like Correxiko?",
  caseStudyHref: "/our-customers/correxiko",
};

export const TRUE_CLASSIC: CaseStudyLplContent = {
  slug: "true-classic",
  metaTitle:
    "How True Classic Reached a 9-Figure Run Rate With Wayflyer | Wayflyer",
  metaDescription:
    "See how True Classic bet on new categories like activewear and grew to a 9-figure run rate with Wayflyer's flexible funding, without raising equity.",
  brand: "True Classic",
  industry: "Apparel",
  location: "USA",
  heroTitle: "True Classic reached a",
  heroHighlight: "",
  heroTitleSuffix: "9-figure run rate",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "True Classic bootstrapped its way to a 9-figure run rate without raising equity. When a bet on activewear strained cash flow, Wayflyer stepped in as the flexible partner that kept them growing.",
  playbackId: "EC00dwAxMzhYtIeZEjYgAhNIz02MRt2mM2rzTTHLbiPL8",
  posterWidth: 1280,
  posterHeight: 720,
  mobilePlaybackId: "yvTWfUILclyIimr00m1bT9uaCNTmyBh6cadJvP1kIVuY",
  spokesperson: {
    name: "Ben Yahalom",
    title: "CEO",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/21407302222468e6e0f9ef2852d04a968b91a1a2-495x495.png",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "A big bet on activewear, a bigger cash problem",
    blurb:
      "Entering a brand new category meant converting cash into inventory long before knowing if it would sell. In 2022, a bet on activewear left True Classic in a cash constraint, with no outside investors to fall back on.",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender who bet on where they were headed",
    blurb:
      "Banks wanted proven history before lending. Wayflyer underwrote where True Classic was headed, not just where it had been, with few restrictions on how or when the cash could be used.",
  },
  results: {
    kicker: "What's Possible For You",
    title: "A 9-figure run rate. Zero equity raised.",
    blurb:
      "Wayflyer floated the cash forward so True Classic could sell through the activewear inventory and rebalance stock, all without raising a single round of equity.",
  },
  resultsEyebrow: "Ready for the next bet",
  resultsQuote:
    "Wayflyer helped us float the cash forward until we could sell through that inventory and rebalance our stock. We would not have been able to do that without them.",
  resultsStats: [
    { value: "9-figure", label: "Annual run rate" },
    { value: "0%", label: "Equity given up" },
    { value: "190+", label: "Markets sold in" },
  ],
  closingTitle: "Ready to grow like True Classic?",
  caseStudyHref: "/our-customers/true-classic",
};

export const SPONGELLE: CaseStudyLplContent = {
  slug: "spongelle",
  metaTitle: "How Spongellé Got Funded in 24 Hours With Wayflyer | Wayflyer",
  metaDescription:
    "See how Spongellé connected its store to Wayflyer and received funding in 24 hours, fast enough to fund new wholesale and big-box retail orders.",
  brand: "Spongellé",
  industry: "Beauty",
  location: "USA",
  heroTitle: "Spongellé got funded in",
  heroHighlight: "24 hours.",
  heroTitleSuffix: "No paperwork. No waiting weeks.",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "Traditional banks meant a long, slow process. Spongellé connected its store and ad accounts to Wayflyer and had funding in 24 hours, fast enough to keep up with new wholesale demand.",
  playbackId: "0102TD5yIkednGJRopnO00obj51XQoIj02cUV8Dc02OCoB00M",
  posterWidth: 1280,
  posterHeight: 720,
  mobilePlaybackId: "NDiknaPZ2TSTleg3ZTntWlKW9BZQlvUjyCfJ7JGSJ78",
  spokesperson: {
    name: "Eric Binder",
    title: "COO",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/1b9e6a47cba6ea13465e39111415aa7664f3bd2f-948x948.png",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "New retailers, old-fashioned funding",
    blurb:
      "Landing new wholesale customers meant funding big purchase orders in advance, but banks were slow, and invoice or PO factoring didn't give Spongellé access to capital until after the fact.",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender as fast as the business needed",
    blurb:
      "Spongellé connected its store and ad accounts to Wayflyer and had funding in 24 hours. No lengthy applications, no back-and-forth, just speed and ease from day one.",
  },
  results: {
    kicker: "What's Possible For You",
    title: "Capital in advance. Any retailer, no problem.",
    blurb:
      "With cash in hand before the POs landed, Spongellé could produce and stock inventory ahead of demand spikes, and take on new big-box retailers without the usual financing scramble.",
  },
  resultsEyebrow: "Ready for the next order",
  resultsQuote:
    "Working with Wayflyer gives us the peace of mind to know we can take on new challenges.",
  resultsStats: [
    { value: "24 hrs", label: "Store connected to funds landing" },
    { value: "0%", label: "Equity given up" },
    { value: "3+", label: "Sales channels funded, DTC to big-box retail" },
  ],
  closingTitle: "Ready to grow like Spongellé?",
  caseStudyHref: "/our-customers/spongelle",
};

export const GHOST_GOLF: CaseStudyLplContent = {
  slug: "ghost-golf",
  metaTitle: "How Ghost Golf Is Fixing Its Black Friday Stockouts | Wayflyer",
  metaDescription:
    "See how Ghost Golf could have sold $2M more during Black Friday, and how Wayflyer's funding means it never runs short again.",
  brand: "Ghost Golf",
  industry: "Sporting Goods",
  location: "USA",
  heroTitle: "Ghost Golf made sure they'd",
  heroHighlight: "",
  heroTitleSuffix: "never run out of stock again.",
  heroYouLine: "So should you.",
  heroSubhead:
    "Ghost Golf could have sold $2M more during Black Friday, but banks couldn't fund the inventory in time. Wayflyer could, and made sure it never ran short again.",
  playbackId: "Y004LOYCOAa7ZhB8YHX2o18fPKsxvVhOtZBpoKa02sDBQ",
  posterWidth: 1280,
  posterHeight: 720,
  mobilePlaybackId: "l73uq01QRF5Eps3S32nrMxoFRVut25NJZqJPJpNE2SBk",
  spokesperson: {
    name: "Andy Nalla",
    title: "Founder",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/dda9b8cc24a1722098b419c57a741d8797e65af7-332x332.png",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "Sold out in a week, with $2M more in demand",
    blurb:
      "Ghost Golf blew past its own sales targets and sold out completely within a week of Black Friday. The only reason: not enough funding to buy the inventory to meet demand.",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender who understood the growth",
    blurb:
      "Banks struggled to understand Ghost Golf's business model and growth trajectory, so they limited how much they'd fund. Wayflyer understood the marketing strategy and the inventory need, and funded accordingly.",
  },
  results: {
    kicker: "What's Possible For You",
    title: "Fully stocked. Never caught short again.",
    blurb:
      "With funding that matches demand instead of lagging behind it, Ghost Golf can go into its next Black Friday stocked for the number it's actually going to hit, not the one it hoped for.",
  },
  resultsEyebrow: "Never again",
  resultsQuote:
    "My biggest regret last year was not having that conversation with Wayflyer sooner.",
  resultsStats: [
    { value: "$2M", label: "In extra demand they couldn't fund in time" },
    { value: "0%", label: "Equity given up" },
    { value: "1 week", label: "Time to sell out after Black Friday" },
  ],
  closingTitle: "Ready to grow like Ghost Golf?",
  caseStudyHref: "/our-customers/ghost-golf",
};

export const PETSHOP_CO_UK: CaseStudyLplContent = {
  slug: "petshop-co-uk",
  metaTitle: "How Petshop.co.uk Grew Its 8-Figure Business by 30% | Wayflyer",
  metaDescription:
    "See how Petshop.co.uk used Wayflyer's funding to bulk-buy stock, lower prices, and grow its 8-figure business by 30%, without giving up equity.",
  brand: "Petshop.co.uk",
  industry: "Pet Food",
  location: "UK",
  heroTitle: "Petshop.co.uk grew their",
  heroHighlight: "",
  heroTitleSuffix: "8-figure business by 30%",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "Petshop.co.uk bootstrapped its way to an 8-figure business over 13 years, but couldn't grow further without extra funding. Wayflyer let them bulk-buy stock, lock in lower prices, and pass the savings on to customers, all without giving up equity.",
  playbackId: "zvjyY01muqPpnT013018p6jtOUHL8Qwb1PihVc9ovseXZs",
  posterWidth: 1280,
  posterHeight: 720,
  mobilePlaybackId: "1FOKW2sqC86XzgMblvb4gn13E46601C2mniYjvfh33II",
  spokesperson: {
    name: "Adam Taylor",
    title: "Co-founder",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/28ce4d487aa8c5c203b83566fcacb8c0293507a6-221x221.png",
  },
  challenge: {
    kicker: "The Growth Ceiling",
    title: "Bootstrapped to 8 figures, and stuck there",
    blurb:
      "Thirteen years of bootstrapping built an 8-figure pet food business, but without extra capital, Petshop.co.uk couldn't buy stock at the scale needed to unlock better prices from suppliers.",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender who let them buy in bulk",
    blurb:
      "Wayflyer's financing let Petshop.co.uk bulk-buy stock and lock in cheaper prices from suppliers, guaranteeing the supply that cash alone couldn't cover.",
  },
  results: {
    kicker: "What's Possible For You",
    title: "30% growth. Full control kept.",
    blurb:
      "Lower prices for customers meant more sales, 30% growth in a tough market, plus room to invest in automation, senior hires, and better software.",
  },
  resultsEyebrow: "Scaling on their own terms",
  resultsQuote:
    "To grow 30% in a tough market and still have complete control is really quite unusual. Wayflyer has genuinely enabled that.",
  resultsStats: [
    { value: "30%", label: "Growth added this year" },
    { value: "0%", label: "Equity given up" },
    { value: "13", label: "Years bootstrapped before Wayflyer" },
  ],
  closingTitle: "Ready to grow like Petshop.co.uk?",
  caseStudyHref: "/our-customers/petshop-co-uk",
};

export const LITTLE_WORDS_PROJECT: CaseStudyLplContent = {
  slug: "little-words-project",
  metaTitle:
    "How Little Words Project Kept Growing After a Bank Pulled Its Credit | Wayflyer",
  metaDescription:
    "See how Little Words Project bootstrapped to $20M, then turned to Wayflyer when a bank called jewelry too risky and pulled their credit line.",
  brand: "Little Words Project",
  industry: "Jewelry",
  location: "USA",
  heroTitle: "Little Words Project",
  heroHighlight: "",
  heroTitleSuffix: "grew to $20M",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "A bank pulled Little Words Project's credit line right after a big holiday season, calling jewelry too risky to lend against. Wayflyer understood the DTC business well enough to know that wasn't true, and funded them fast.",
  playbackId: "x9RP47vs6uHqrDo24WnZ4IKTSQAcXTXC02uSrbK9eo028",
  posterWidth: 1280,
  posterHeight: 720,
  mobilePlaybackId: "O0254gK01R00pVHqQ47PZStGWn002l00i2oPebQ802gIxyZfw",
  spokesperson: {
    name: "Adriana Carrig",
    title: "Founder",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/8c02e74d09a6d4388bd2500d0658652fbb15e522-888x888.png",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "Too risky, the bank said. Right when it mattered most.",
    blurb:
      "Right after a big holiday season, with inventory to pay for and payroll to meet, the bank pulled Little Words Project's credit line, calling jewelry too risky an asset class to lend against.",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender who actually understood DTC",
    blurb:
      "Wayflyer plugged straight into Little Words Project's platforms and underwrote them fast. Where the bank saw an unfamiliar category, Wayflyer saw a business it understood, and knew it wasn't a risk at all.",
  },
  results: {
    kicker: "What's Possible For You",
    title: "15 stores now. 100 doors in sight.",
    blurb:
      "With funding that didn't disappear when it mattered most, Little Words Project kept growing into retail, from about 15 stores today toward a clear line of sight on 50 to 100 doors.",
  },
  resultsEyebrow: "Saved the day",
  resultsQuote:
    "We felt like the rug had been pulled out from under us. But Wayflyer really swooped in and saved the day.",
  resultsStats: [
    { value: "$20M", label: "Bootstrapped before Wayflyer" },
    { value: "0%", label: "Equity given up" },
    { value: "50-100", label: "Retail doors in sight" },
  ],
  closingTitle: "Ready to grow like Little Words Project?",
  caseStudyHref: "/our-customers/little-words-project",
};

export const KEKOA_FOODS: CaseStudyLplContent = {
  slug: "kekoa-foods",
  metaTitle: "How Kekoa Foods Went From 1 Store to 800 in a Year | Wayflyer",
  metaDescription:
    "See how Kekoa Foods used Wayflyer's funding to bridge the gap between production and retailer payment terms, growing from 1 store to 800 in a year.",
  brand: "Kekoa Foods",
  industry: "Baby Food",
  location: "USA",
  heroTitle: "Kekoa Foods went",
  heroHighlight: "",
  heroTitleSuffix: "from 1 store to 800",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "Every new retailer meant funding inventory weeks before a single PO paid out. Wayflyer bridged that gap, letting Kekoa Foods go from one store to 800 in a year without slowing down for cash.",
  playbackId: "9dpBqzFuO5LzhkBWAcwyWCyag1oaopCCqCO5h6h8JV8",
  posterWidth: 1280,
  posterHeight: 720,
  mobilePlaybackId: "PtD500HHdo01UJXwF02FZz5BwYph3xQJwTbW3CDYF4C5tc",
  spokesperson: {
    name: "David Fullner",
    title: "Co-founder",
    avatarSrc:
      "https://cdn.sanity.io/images/1aendizt/redesign/94a0bd3835d16533f7e73fa8ffe65d1b9ff869e6-588x588.png",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "Weeks to make it, days to deliver it",
    blurb:
      "Production takes six to eight weeks, but once a distributor wants the product, it needs to be on a truck within days. Fronting all that cash before a single PO paid out was stressful, and risky to get wrong.",
  },
  solution: {
    kicker: "What Changed",
    title: "A lender who bridged the gap",
    blurb:
      "When a buyer said yes to a national rollout on the spot, Wayflyer had the funding ready to actually create the product, bridging the gap from pouch creation to shelf to getting paid.",
  },
  results: {
    kicker: "What's Possible For You",
    title: "1 store to 800. And still climbing.",
    blurb:
      "With inventory funded ahead of demand instead of behind it, Kekoa Foods went from a single store to about 800 in a year, and is aiming for 800 more next.",
  },
  resultsEyebrow: "Onward to the next 800",
  resultsQuote:
    "Everyone was super personable. They really wanted our brand to succeed, and were willing to find terms that worked well for our company.",
  resultsStats: [
    { value: "1 to 800", label: "Stores in a year" },
    { value: "0%", label: "Equity given up" },
    { value: "6-8 wks", label: "Production time funded ahead of each PO" },
  ],
  closingTitle: "Ready to grow like Kekoa Foods?",
  caseStudyHref: "/our-customers/kekoa-foods",
};

export const THREE_BEARS: CaseStudyLplContent = {
  slug: "3bears",
  metaTitle:
    "How 3Bears Used Wayflyer to Enter 1,000 New Stores at Once | Wayflyer",
  metaDescription:
    "See how 3Bears used Wayflyer funding to enter 1,000 new stores at once during its first international expansion, without giving up equity.",
  brand: "3Bears",
  industry: "Oats & Breakfast",
  location: "Germany",
  heroTitle: "3Bears used Wayflyer to enter",
  heroHighlight: "",
  heroTitleSuffix: "1,000 new stores at once.",
  heroYouLine: "Your brand could be next.",
  heroSubhead:
    "Traditional lenders needed a track record 3Bears didn't have yet. Wayflyer looked at where the business was headed and funded the growth instead, fast enough to enter 1,000 new stores at once.",
  playbackId: "QZ8XL86X5utg02REqjUyiz16duW9igY02SOVfaMfkMGM00",
  mobilePlaybackId: "MBtHkNTUfDA4ThiwbSzXA1sqQIS6cZK00KKSrhx2d7004",
  posterWidth: 1280,
  posterHeight: 720,
  spokesperson: {
    name: "Caroline Nichols",
    title: "Co-founder",
    avatarSrc: "/images/case-studies/3bears/caroline-nichols.png",
  },
  challenge: {
    kicker: "Sound Familiar?",
    title: "One TV show, then every retailer wanted in",
    blurb:
      "After one appearance on Germany's Die Höhle der Löwen, retailers across the country wanted 3Bears on shelf at once. German banks lend against collateral and years of track record, and a young porridge brand fresh off a TV pitch had neither.",
    backgroundSrc: "/images/case-studies/3bears/timeline-challenge.jpg",
  },
  solution: {
    kicker: "What Changed",
    title: "A funder who looked at where they were headed",
    blurb:
      "Traditional lenders wanted a financial history 3Bears hadn't built yet. Wayflyer looked at the trajectory instead, the orders already on the books and the retailers already asking, and funded the growth fast enough to keep up, without the personal guarantees German banks required.",
    backgroundSrc: "/images/case-studies/3bears/timeline-solution.jpg",
  },
  results: {
    kicker: "What's Possible For You",
    title: "1,000 new stores, entered in one go",
    blurb:
      "That same fast, flexible funding scaled with 3Bears. When the brand expanded internationally for the first time, Wayflyer funded the working capital behind entering 1,000 additional stores at once, no equity given up, no months-long bank approval.",
    backgroundSrc: "/images/case-studies/3bears/timeline-results.jpg",
  },
  resultsEyebrow: "Onward across Europe",
  resultsQuote:
    "They do look at your track record to some extent, but they're also able to finance flexible ideas and visions very quickly. That's how we can grow.",
  resultsStats: [
    { value: "1,000", label: "New stores entered at once" },
    { value: "0%", label: "Equity given up" },
    {
      value: "6",
      label:
        "New markets: Netherlands, Austria, Switzerland, Belgium, the UAE, Iceland",
    },
  ],
  closingTitle: "Ready to grow like 3Bears?",
  caseStudyHref: "/our-customers/3bears",
};

/**
 * German translation of THREE_BEARS, used only on the `de` locale (see
 * resolveCaseStudyContent below). 3Bears is the only case study with a
 * de-locale version today: es/nl are intentionally not covered. This is a
 * separate object rather than locale-keyed fields on CaseStudyLplContent
 * because every field needs translating anyway (not just the video), so
 * a parallel object is simpler than threading a locale branch through
 * every field on a shared interface used by the other 7 brand pages.
 */
export const THREE_BEARS_DE: CaseStudyLplContent = {
  slug: "3bears",
  metaTitle:
    "Wie 3Bears mit Wayflyer auf einen Schlag 1.000 neue Filialen belieferte | Wayflyer",
  metaDescription:
    "Erfahre, wie 3Bears mit Wayflyer-Finanzierung während der ersten internationalen Expansion auf einen Schlag 1.000 neue Filialen belieferte, ohne Anteile abzugeben.",
  brand: "3Bears",
  industry: "Hafer & Frühstück",
  location: "Deutschland",
  heroTitle: "3Bears nutzte Wayflyer, um",
  heroHighlight: "",
  heroTitleSuffix: "1.000 neue Filialen auf einen Schlag zu beliefern.",
  heroYouLine: "Deine Marke könnte die nächste sein.",
  heroSubhead:
    "Klassische Kreditgeber verlangten eine Erfolgsbilanz, die 3Bears noch nicht vorweisen konnte. Wayflyer schaute stattdessen darauf, wohin sich das Unternehmen entwickelte, und finanzierte das Wachstum schnell genug, um auf einen Schlag 1.000 neue Filialen zu beliefern.",
  playbackId: "02kqA801ABA01YLi77ID174duqoifmw5GH500pfq02udmF4g",
  mobilePlaybackId: "ZQ5cDhIHSMZfqyEG2G002iqxoatyEWyDB1sh01EBSAK01A",
  posterWidth: 1280,
  posterHeight: 720,
  spokesperson: {
    name: "Caroline Nichols",
    title: "Mitgründerin",
    avatarSrc: "/images/case-studies/3bears/caroline-nichols.png",
  },
  challenge: {
    kicker: "Kommt dir das bekannt vor?",
    title: "Eine Fernsehsendung, und plötzlich wollte jeder Händler dabei sein",
    blurb:
      "Nach einem Auftritt bei Die Höhle der Löwen wollten Händler in ganz Deutschland 3Bears auf einen Schlag ins Regal stellen. Deutsche Banken vergeben Kredite gegen Sicherheiten und jahrelange Erfolgsbilanz, und eine junge Porridge-Marke frisch nach einem TV-Pitch hatte beides nicht.",
    backgroundSrc: "/images/case-studies/3bears/timeline-challenge.jpg",
  },
  solution: {
    kicker: "Was sich änderte",
    title: "Ein Finanzierer, der auf die Zukunft schaute",
    blurb:
      "Klassische Kreditgeber verlangten eine Finanzhistorie, die 3Bears noch nicht aufgebaut hatte. Wayflyer schaute stattdessen auf die Entwicklung, die bereits eingegangenen Bestellungen und die bereits anfragenden Händler, und finanzierte das Wachstum schnell genug, um mitzuhalten, ohne die persönlichen Bürgschaften, die deutsche Banken verlangten.",
    backgroundSrc: "/images/case-studies/3bears/timeline-solution.jpg",
  },
  results: {
    kicker: "Was für dich möglich ist",
    title: "1.000 neue Filialen auf einen Schlag",
    blurb:
      "Dieselbe schnelle, flexible Finanzierung wuchs mit 3Bears mit. Als die Marke zum ersten Mal international expandierte, finanzierte Wayflyer das Betriebskapital für den Einstieg in 1.000 zusätzliche Filialen auf einen Schlag, ohne Anteile abzugeben und ohne monatelange Bankprüfung.",
    backgroundSrc: "/images/case-studies/3bears/timeline-results.jpg",
  },
  resultsEyebrow: "Weiter durch Europa",
  resultsQuote:
    "Sie schauen sich zwar auch die Erfolgsbilanz an, aber sie können auch flexible Ideen und Visionen sehr schnell finanzieren. So können wir wachsen.",
  resultsStats: [
    { value: "1.000", label: "Neue Filialen auf einen Schlag" },
    { value: "0%", label: "Abgegebene Anteile" },
    {
      value: "6",
      label:
        "Neue Märkte: Niederlande, Österreich, Schweiz, Belgien, die VAE, Island",
    },
  ],
  closingTitle: "Bereit, wie 3Bears zu wachsen?",
  caseStudyHref: "/our-customers/3bears",
};

/** Add each new brand's content here to publish it at /grow-with-wayflyer/{slug}. */
const ALL_CASE_STUDIES: CaseStudyLplContent[] = [
  SAND_CLOUD,
  CORREXIKO,
  TRUE_CLASSIC,
  SPONGELLE,
  GHOST_GOLF,
  PETSHOP_CO_UK,
  LITTLE_WORDS_PROJECT,
  KEKOA_FOODS,
  THREE_BEARS,
];

export const CASE_STUDY_LPL_CONTENT: Record<string, CaseStudyLplContent> =
  Object.fromEntries(ALL_CASE_STUDIES.map((study) => [study.slug, study]));

/**
 * Locale-specific overrides, keyed by slug then locale. Only 3Bears/de
 * exists today: es/nl are intentionally not covered, and no other case
 * study has a non-English version. Add further entries here as more
 * brands get translated locales.
 */
const CASE_STUDY_LOCALE_OVERRIDES: Record<
  string,
  Record<string, CaseStudyLplContent>
> = {
  "3bears": { de: THREE_BEARS_DE },
};

/**
 * Resolves the content for a case study page, honoring locale overrides
 * (e.g. 3Bears/de) and falling back to the default English content for
 * any EN_ONLY_LOCALES locale. Returns undefined when the slug doesn't
 * exist or the locale isn't available for it, so callers can treat that
 * as the single source of truth for notFound().
 */
export function resolveCaseStudyContent(
  slug: string,
  lang: string
): CaseStudyLplContent | undefined {
  const override = CASE_STUDY_LOCALE_OVERRIDES[slug]?.[lang];
  if (override) return override;
  if (!isLocaleAllowedForEnOnly(lang)) return undefined;
  if (!Object.hasOwn(CASE_STUDY_LPL_CONTENT, slug)) return undefined;
  return CASE_STUDY_LPL_CONTENT[slug];
}

/** Extra locales (beyond EN_ONLY_LOCALES) available for a given case study, for hreflang alternates. */
export function getExtraCaseStudyLocales(slug: string): string[] {
  return Object.keys(CASE_STUDY_LOCALE_OVERRIDES[slug] ?? {});
}

/**
 * Local copy of profitmill-lpl's FAQS with all em-dashes stripped.
 * Kept separate from the shared file so we don't alter copy on the
 * generic /flexible-funding page.
 */
export const CASE_STUDY_FAQS = [
  {
    q: "What do I need to qualify?",
    a: "You need at least 12 months of trading history, $10K+ in monthly revenue, and operations in the US, UK, or Australia in a supported industry (eCommerce, retail, healthcare, SaaS and more).",
  },
  {
    q: "How much can I borrow?",
    a: "Wayflyer funds anywhere from $5,000 up to $20 million. The exact amount we can offer you depends on your sales data and what we believe you can comfortably repay.",
  },
  {
    q: "What are Wayflyer’s fees?",
    a: "One transparent flat fee, agreed upfront. No compounding interest, no late fees, no hidden charges. Your repayment total is fixed from day one.",
  },
  {
    q: "What can I use the funding for?",
    a: "Anything that helps your business grow: inventory, marketing, payroll, equipment, expansion. There are no spend restrictions.",
  },
  {
    q: "What happens if my sales slow down?",
    a: "If you’re on our Cash Advance product, repayments flex with your sales, so slower months mean smaller payments. We work with you on a plan if needed.",
  },
  {
    q: "Do I need collateral, or to give equity or a personal guarantee?",
    a: "No. Wayflyer funding is unsecured: no collateral, no equity given up, and no personal guarantees from founders.",
  },
  {
    q: "Does applying affect my credit score?",
    a: "No. Reviewing your offer with Wayflyer does not impact your personal credit score in any way.",
  },
  {
    q: "How quickly can I access funds?",
    a: "Most customers receive their funds in as little as 24 hours after accepting an offer.",
  },
  {
    q: "How do I make repayments?",
    a: "Repayments are managed through your Wayflyer dashboard. Set them to bi-weekly or monthly, whichever works best for your cashflow.",
  },
  {
    q: "Can I repay the loan early?",
    a: "Yes. You can pay off early at any time directly from your dashboard, with no early repayment penalties.",
  },
  {
    q: "Can I get more funding later?",
    a: "Yes. Most customers come back for additional rounds. Once you’re a Wayflyer customer, accessing more capital is fast and easy.",
  },
  {
    q: "Can I get more than one round of funding?",
    a: "Absolutely. Many of our customers run several rounds in parallel across different products like Cash Advance and Rolling Financing.",
  },
  {
    q: "Can I talk to a real person if I need help?",
    a: "Yes. Every Wayflyer customer gets a dedicated account manager and access to our support team, in addition to the in-app help center.",
  },
  {
    q: "Who backs Wayflyer?",
    a: "Wayflyer is backed by top-tier institutional partners including J.P. Morgan, with billions of dollars available to deploy to growing brands.",
  },
];

/**
 * German translation of CASE_STUDY_FAQS. Written directly in euros
 * rather than run through localizeCurrency, since this array is only
 * ever used on de. Kept in the same order as the English version.
 */
export const CASE_STUDY_FAQS_DE = [
  {
    q: "Was brauche ich, um infrage zu kommen?",
    a: "Du benötigst mindestens 12 Monate Handelshistorie, mindestens 10.000 € monatlichen Umsatz und einen Geschäftssitz in den USA, UK oder Australien in einer unterstützten Branche (eCommerce, Einzelhandel, Gesundheitswesen, SaaS und mehr).",
  },
  {
    q: "Wie viel kann ich leihen?",
    a: "Wayflyer finanziert zwischen 5.000 € und 20 Millionen €. Der genaue Betrag, den wir dir anbieten können, hängt von deinen Verkaufsdaten ab und davon, was du unserer Einschätzung nach komfortabel zurückzahlen kannst.",
  },
  {
    q: "Welche Gebühren erhebt Wayflyer?",
    a: "Eine transparente Pauschalgebühr, im Voraus vereinbart. Keine Zinseszinsen, keine Verzugsgebühren, keine versteckten Kosten. Dein gesamter Rückzahlungsbetrag steht von Anfang an fest.",
  },
  {
    q: "Wofür kann ich die Finanzierung nutzen?",
    a: "Alles, was deinem Unternehmen beim Wachstum hilft: Lagerbestände, Marketing, Gehälter, Ausrüstung, Expansion. Es gibt keine Ausgabenbeschränkungen.",
  },
  {
    q: "Was passiert, wenn mein Umsatz zurückgeht?",
    a: "Wenn du unser Cash-Advance-Produkt nutzt, passen sich die Rückzahlungen an deinen Umsatz an, sodass schwächere Monate kleinere Raten bedeuten. Bei Bedarf erarbeiten wir gemeinsam mit dir einen Plan.",
  },
  {
    q: "Brauche ich Sicherheiten, muss ich Anteile abgeben oder eine persönliche Bürgschaft leisten?",
    a: "Nein. Die Finanzierung von Wayflyer ist unbesichert: keine Sicherheiten, keine Anteile abgeben, keine persönlichen Bürgschaften der Gründer.",
  },
  {
    q: "Wirkt sich die Antragstellung auf meine Kreditwürdigkeit aus?",
    a: "Nein. Die Prüfung deines Angebots durch Wayflyer hat keinerlei Auswirkung auf deine persönliche Kreditwürdigkeit.",
  },
  {
    q: "Wie schnell erhalte ich die Mittel?",
    a: "Die meisten Kunden erhalten ihre Mittel bereits 24 Stunden nach Annahme eines Angebots.",
  },
  {
    q: "Wie erfolgen die Rückzahlungen?",
    a: "Rückzahlungen werden über dein Wayflyer-Dashboard verwaltet. Stelle sie auf zweiwöchentlich oder monatlich ein, je nachdem, was für deinen Cashflow am besten passt.",
  },
  {
    q: "Kann ich die Finanzierung vorzeitig zurückzahlen?",
    a: "Ja. Du kannst jederzeit direkt über dein Dashboard vorzeitig zurückzahlen, ganz ohne Vorfälligkeitsentschädigung.",
  },
  {
    q: "Kann ich später weitere Finanzierung erhalten?",
    a: "Ja. Die meisten Kunden kommen für weitere Runden zurück. Sobald du Wayflyer-Kunde bist, ist der Zugang zu weiterem Kapital schnell und unkompliziert.",
  },
  {
    q: "Kann ich mehr als eine Finanzierungsrunde gleichzeitig erhalten?",
    a: "Auf jeden Fall. Viele unserer Kunden führen mehrere Runden parallel über verschiedene Produkte wie Cash Advance und Rolling Financing.",
  },
  {
    q: "Kann ich mit einer echten Person sprechen, wenn ich Hilfe brauche?",
    a: "Ja. Jeder Wayflyer-Kunde erhält einen persönlichen Account Manager und Zugang zu unserem Support-Team, zusätzlich zum Hilfe-Center in der App.",
  },
  {
    q: "Wer steht hinter Wayflyer?",
    a: "Wayflyer wird von erstklassigen institutionellen Partnern wie J.P. Morgan unterstützt, mit Milliarden an Dollar, die für wachsende Marken bereitstehen.",
  },
];
